import React, { createContext, useContext, useState, useEffect } from 'react'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showProfileCompletion, setShowProfileCompletion] = useState(false)

  useEffect(() => {
    // Check for existing auth token on app load
    const token = localStorage.getItem('authToken')
    if (token) {
      // Verify token and get user data
      verifyToken(token)
    } else {
      setLoading(false)
    }
  }, [])

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUser(data.user)
        setSubscription(data.subscription || null) // Explicitly set to null if undefined
        
        // Check if user needs to complete profile
        if (data.user.profileCompletionRequired) {
          setShowProfileCompletion(true)
        }
      } else {
        // If token is invalid, clear everything
        localStorage.removeItem('authToken')
        setUser(null)
        setSubscription(null)
      }
    } catch (error) {
      console.error('Token verification failed:', error)
      localStorage.removeItem('authToken')
      setUser(null)
      setSubscription(null)
    } finally {
      setLoading(false)
    }
  }

  const login = async (email, password) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      })

      if (response.ok) {
        const data = await response.json()
        localStorage.setItem('authToken', data.token)
        setUser(data.user)
        setSubscription(data.subscription)
        
        // Check if user needs to complete profile
        if (data.user.profileCompletionRequired) {
          setShowProfileCompletion(true)
        }
        
        return { success: true }
      } else {
        // Try to parse error response, but handle non-JSON gracefully
        let errorMessage = 'Login failed'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch (parseError) {
          // If response isn't JSON, use status text
          errorMessage = response.statusText || errorMessage
        }
        return { success: false, error: errorMessage }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, error: 'Network error. Please try again.' }
    }
  }

  const register = async (userData) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      })

      if (response.ok) {
        const data = await response.json()
        localStorage.setItem('authToken', data.token)
        setUser(data.user)
        setSubscription(data.subscription)
        
        // New users will need to complete profile
        setShowProfileCompletion(true)
        
        return { success: true }
      } else {
        // Try to parse error response, but handle non-JSON gracefully
        let errorMessage = 'Registration failed'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch (parseError) {
          // If response isn't JSON, use status text
          errorMessage = response.statusText || errorMessage
        }
        return { success: false, error: errorMessage }
      }
    } catch (error) {
      console.error('Registration error:', error)
      return { success: false, error: 'Network error. Please try again.' }
    }
  }

  const logout = () => {
    localStorage.removeItem('authToken')
    setUser(null)
    setSubscription(null)
  }

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData)
  }

  const handleProfileCompletion = (completedUser) => {
    setUser(completedUser)
    setShowProfileCompletion(false)
  }

  const skipProfileCompletion = () => {
    setShowProfileCompletion(false)
  }

  const refreshSubscription = async () => {
    const token = localStorage.getItem('authToken')
    if (!token) return

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUser(data.user)
        setSubscription(data.subscription)
      }
    } catch (error) {
      console.error('Error refreshing subscription:', error)
    }
  }

  const value = {
    user,
    subscription,
    loading,
    showProfileCompletion,
    login,
    register,
    logout,
    updateUser,
    handleProfileCompletion,
    skipProfileCompletion,
    refreshSubscription
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
